<template>
  <div class="theme-teasers">
    <Heading
      class="theme-teasers__heading"
      tag="h2"
      size="md"
      font-weight="regular"
      spaces-bottom-rem
    >
      Themenwelten
    </Heading>
    <div class="theme-teasers__list">
      <template
        v-for="(teaser, id) in teasersData"
        :key="id"
      >
        <MouseOverTeaser
          class="theme-teaser"
          :class="teaser.backgroundClass"
          :brand="teaser.brand"
          :card="teaser.card"
        >
          <template #image="{ mouseOver }">
            <CdnImage
              v-for="image in teaser.contentImages"
              v-bind="image"
              :key="image.src"
              class="mouseover-teaser__element"
              :style="image.styles"
              :class="{
                [image.animationClass]: mouseOver,
              }"
              lazy
            />
          </template>
        </MouseOverTeaser>
      </template>
    </div>

    <LinkButton
      tag="nuxt-link"
      to="/themenwelten"
      no-styling
      class="theme-teasers__note"
    >
      Weitere Themenwelten entdecken
      <span class="theme-teasers__arrow-symbol"
        ><Arrow
          direction="right"
          tag="span"
      /></span>
    </LinkButton>
  </div>
</template>

<script setup lang="ts">
import MouseOverTeaser from '~/components/mouseover-teaser.vue';
import {
  Arrow,
  Heading,
  LinkButton,
  type CardComponentProps,
} from '@i22team/td-longtail-vue-components';
import CdnImage, { type CdnImageProps } from '~/components/cdn-image.vue';
import { addLogo, type BrandWithLogo } from '~/lib/brand';

type Teaser = {
  card?: CardComponentProps;
  contentImages: (CdnImageProps & {
    styles?: string | Record<string, any>;
    animationClass: string;
  })[];
  brand?: BrandWithLogo;
  backgroundClass?: string;
};

const teasersData: Teaser[] = [
  {
    card: {
      href: '/anker',
      heading: {
        tag: 'h3',
        size: 'md',
        fontWeight: 'ultra',
        text: 'Die Nr.1 für mobiles Laden',
      },
      background: {
        alt: 'Anker Ladegeräte',
        src: '/content/content-images/homepage/theme-teasers/1bf7d247738133107a006c8e0f11778d/anker-themenweltteaser-bg@2x.jpg',
        sizes: 'sm:100vw md:50vw lg:928px',
        lazy: true,
      },
    },
    brand: addLogo({
      name: 'Anker Logo',
      slug: 'anker',
    }),

    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/ed9192f25951f0fc4546dde13348fd57/anker-themenweltteaser-produkte@2x.png',
        alt: 'Anker Ladegeräte',
        aspectRatio: '836 / 675',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:78vw sm:80vw md:50vw lg:464px',
        lazy: true,
      },
    ],
  },
  {
    card: {
      href: '/a-good-company',
      heading: {
        tag: 'h3',
        size: 'md',
        fontWeight: 'ultra',
        text: 'Schütze dein Smartphone und die Umwelt',
      },
      background: {
        alt: 'Eine durchsichtige Handyhülle von agood company unter einem Smartphone',
        src: '/content/content-images/homepage/theme-teasers/ec88e22714324dd5e23b84890cce72c7/agood-themenwelt-teaser-bg@2x.jpg',
        sizes: 'sm:100vw md:50vw lg:928px',
        lazy: true,
      },
    },
    brand: addLogo({
      name: 'agood company Logo',
      slug: 'agood',
    }),

    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/70baed837d7482161626ebc31739c99c/agood-case-no-plastic-green-magenta@2x.png',
        alt: 'Handyhülle von agood company',
        aspectRatio: '338 / 539',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:60vw sm:80vw md:50vw lg:464px',
        lazy: true,
      },
    ],
  },
  {
    card: {
      href: '/panzerglass',
      heading: {
        tag: 'h3',
        size: 'md',
        fontWeight: 'ultra',
        text: 'Die Lösung für dein Displayschutz',
      },
      customStyles: '--mouse-background: linear-gradient(219.01deg, #DAE7EF 0%, #B3C4CE 100%);',
    },
    brand: addLogo({
      name: 'PanzerGlass Logo',
      slug: 'panzerglass',
    }),
    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/69969af05fd20c73dfa65f813283785c/panzerglass-homepage-teaser.png',
        alt: 'PanzerGlass Produkte',
        aspectRatio: ' 464 / 444',
        styles: 'padding: 12px;',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:60vw sm:80vw md:50vw lg:464px',
        lazy: true,
      },
    ],
  },
];
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.theme-teasers {
  padding-inline: $grid-base;
  &__heading {
    z-index: 1;
  }

  &__list {
    display: grid;
    gap: $grid-base;
    grid-template-columns: 1fr;
    margin-bottom: 1rem;

    @include mq($mq-sm) {
      grid-template-columns: 1fr 1fr;
      grid-gap: $grid-base * 2;
    }
    @include mq($mq-md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__note {
    color: var(--color-font);
    display: flex;

    &:hover {
      .theme-teasers__arrow-symbol {
        &::before {
          min-width: $grid-base;
        }
      }
    }
  }
  &__arrow-symbol {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: $grid-base;
    gap: 2px;
    bottom: 1px;
    &::before {
      content: '';
      border-top: 1px solid var(--color-font);
      position: relative;
      display: block;
      align-self: center;
      min-width: 0;
      width: auto;

      transition: min-width 0.3s ease-out;
      will-change: min-width;
    }
    &--visible {
      &::before {
        min-width: $grid-base;
      }
    }

    .arrow {
      position: relative;
      width: 0.7em;
      height: 0.7em;
    }
  }
}
.theme-teaser.mouseover-teaser {
  --card-image-container-height: 400px;
  --card-image-padding: 0;
  --card-container-content-flex: 0;
  --card-container-flex: 0;
  --card-bg-left: -15px;
  --card-bg-right: -15px;
  --card-bg-width: calc(100% + 30px);
  --mouse-background: linear-gradient(
    219.01deg,
    var(--color-gray-200) 0%,
    var(--color-gray-100) 100%
  );

  :deep(.card__background img) {
    max-width: none;
  }

  &:hover {
    :deep(.card__background img) {
      transform: scale(1.1) rotate(5deg);
    }
  }
}
</style>
